import React, { FC } from 'react';
import { useIntlFormat } from 'utils/intl/useIntlFormat';
import {
  Avatar as AvatarWithoutBadge,
  AvatarWithBadge,
  AvatarProps as BpAvatarProps,
  Tooltip,
  TooltipProps,
  BadgeProps
} from '@beeline.com/bp-design-system';
import { getStringAvatarColor as getAvatarBackgroundColor } from './subcomponents/getAvatarProperties';
import { AvatarSize } from './Avatar.types';

export interface AvatarProps extends Omit<BpAvatarProps, 'slotProps'> {
  // Provide an initial if you want them to be displayed instead of the generic avatar
  initials?: string | null;
  // Pass entity uid to get always the same background color
  backgroundColorUid?: string;
  src?: string;
  defaultSrc?: string;
  size?: AvatarSize;
  margin?: string;
  statusColor?: string;
  lazyLoading?: 'lazy' | 'eager';
  slotProps?: {
    Status?: BadgeProps;
    Tooltip?: Omit<TooltipProps, 'children'>;
  };
}

export const Avatar: FC<React.PropsWithChildren<AvatarProps>> = ({
  size = 'm',
  variant = 'circular',
  initials,
  backgroundColorUid,
  defaultSrc,
  ref,
  src,
  alt,
  lazyLoading = 'lazy',
  slotProps,
  statusColor,
  ...avatarProps
}) => {
  const format = useIntlFormat();
  const TargetComponent = statusColor ? AvatarWithBadge : AvatarWithoutBadge;

  return (
    <WithTooltip {...slotProps?.Tooltip}>
      <TargetComponent
        size={size}
        variant={variant}
        {...(statusColor ? { badgeColor: statusColor } : undefined)}
        src={src}
        data-avatar
        data-avatar-initials={!!initials ? initials : undefined}
        alt={alt ?? format('components.layout.header.header.userAvatar')}
        aria-label={alt ?? format('components.layout.header.header.userAvatar')}
        {...avatarProps}
        role="presentation"
        style={{
          ...avatarProps.style,
          backgroundColor: getAvatarBackgroundColor(backgroundColorUid)
        }}
      >
        {!!initials ? (getEnvAwareInitials(initials) ?? undefined) : undefined}
      </TargetComponent>
    </WithTooltip>
  );
};

export default Avatar;

function WithTooltip(tooltipProps?: TooltipProps) {
  if (!tooltipProps?.title) {
    return tooltipProps?.children;
  }
  return <Tooltip {...tooltipProps} />;
}

function getEnvAwareInitials(initials?: string | null): string | undefined | null {
  if (!initials) {
    return initials;
  }
  return process.env.NODE_ENV !== 'test' ? initials : '';
}
